import { useRef, useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Button, Checkbox, Badge, Tooltip, TooltipContent, TooltipTrigger, cn } from '@pantry/ui';
import { GripVerticalIcon, XIcon, StickyNoteIcon } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import type { ShoppingListItem, ShoppingListWithItems } from '@pantry/shared';
import {
  useUpdateShoppingListItem,
  useDeleteShoppingListItem,
  queryKeys,
} from '@pantry/api-client';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toast } from 'sonner';

interface ShoppingListItemRowProps {
  item: ShoppingListItem;
  householdId: string;
  listId: string;
}

/**
 * A single item row in the shopping list detail view.
 * Supports check/uncheck (optimistic), drag-to-reorder, and delete.
 */
export function ShoppingListItemRow({ item, householdId, listId }: ShoppingListItemRowProps) {
  const { t } = useLingui();

  const queryClient = useQueryClient();
  const updateItem = useUpdateShoppingListItem();
  const deleteItem = useDeleteShoppingListItem();
  const { track } = useAnalytics();
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isPendingDelete, setIsPendingDelete] = useState(false);

  useEffect(() => {
    return () => {
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current);
      }
    };
  }, []);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCheck = (checked: boolean | 'indeterminate') => {
    if (checked === 'indeterminate') return;
    track(
      checked
        ? AnalyticsEvents.SHOPPING_LIST_ITEM_CHECKED
        : AnalyticsEvents.SHOPPING_LIST_ITEM_UNCHECKED,
      { householdId, listId }
    );
    updateItem.mutate(
      {
        householdId,
        listId,
        itemId: item.id,
        data: { isChecked: checked },
      },
      {
        onError: () => {
          toast.error(t`Failed to update. Please try again.`);
        },
      }
    );
  };

  const handleDelete = () => {
    const queryKey = queryKeys.shoppingLists.detail(householdId, listId);
    const snapshot = queryClient.getQueryData<ShoppingListWithItems>(queryKey);

    setIsPendingDelete(true);

    toast(t`Item removed`, {
      duration: 3000,
      action: {
        label: t`Undo`,
        onClick: () => {
          clearTimeout(deleteTimerRef.current);
          setIsPendingDelete(false);
        },
      },
    });

    deleteTimerRef.current = setTimeout(() => {
      deleteItem.mutate(
        { householdId, listId, itemId: item.id },
        {
          onError: () => {
            queryClient.setQueryData(queryKey, snapshot);
            setIsPendingDelete(false);
            toast.error(t`Failed to remove item. Please try again.`);
          },
        }
      );
    }, 3000);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg bg-card border border-border transition-all',
        isDragging && 'opacity-50 shadow-lg z-50',
        item.isChecked && 'opacity-50',
        isPendingDelete && 'opacity-30 pointer-events-none'
      )}
    >
      {/* Drag handle */}
      {!item.isChecked && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          aria-label={t`Drag to reorder`}
        >
          <GripVerticalIcon className="h-4 w-4" />
        </button>
      )}

      {/* Checkbox */}
      <Checkbox
        id={`item-${item.id}`}
        checked={item.isChecked}
        onCheckedChange={handleCheck}
        disabled={updateItem.isPending}
      />

      {/* Item name + quantity */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <label
          htmlFor={`item-${item.id}`}
          className={cn(
            'text-sm font-medium cursor-pointer truncate',
            item.isChecked && 'line-through text-muted-foreground',
            isPendingDelete && 'line-through'
          )}
        >
          {item.displayName}
        </label>

        {(item.quantity !== 1 || (item.unit && item.unit !== 'item')) && (
          <Badge variant="secondary" className="text-xs shrink-0">
            {item.quantity} {item.unit}
          </Badge>
        )}

        {item.notes && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground shrink-0"
                aria-label={t`View notes`}
              >
                <StickyNoteIcon className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-sm">{item.notes}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
        onClick={handleDelete}
        disabled={deleteItem.isPending}
        aria-label={t`Remove ${item.displayName}`}
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
